import { useState } from 'react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { Navigate } from 'react-router-dom';
import {
    ClipboardDocumentListIcon,
    ChartBarIcon,
    ChartPieIcon,
} from '@heroicons/react/24/outline';

import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types';
import { ReportFilters } from '../services/reportService';

import AdvancedFilters from '../components/reports/AdvancedFilters';
import OperationalReports from '../components/reports/OperationalReports';
import AnalyticsReports from '../components/reports/AnalyticsReports';
import InventoryReports from '../components/reports/InventoryReports';
import ReportPreviewModal from '../components/reports/ReportPreviewModal';

const ReportsPage = () => {
    const { user } = useAuth();

    const [activeSection, setActiveSection] = useState<'operational' | 'analytics' | 'inventory'>('operational');

    const [filters, setFilters] = useState<ReportFilters>({
        startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(new Date()), 'yyyy-MM-dd')
    });
    
    // We pass appliedFilters to the children so they only re-fetch when "Apply" is clicked
    const [appliedFilters, setAppliedFilters] = useState<ReportFilters>(filters);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewTitle, setPreviewTitle] = useState('');

    if (user?.rol_usr !== UserRole.ADMIN) {
        return <Navigate to="/dashboard" replace />;
    }

    const handleApplyFilters = () => {
        setAppliedFilters(filters);
    };

    const handlePreview = (url: string, title: string) => {
        setPreviewUrl(url);
        setPreviewTitle(title);
        setPreviewOpen(true);
    };

    return (
        <Layout>
            <div className="space-y-6 max-w-7xl mx-auto px-4 pb-12">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row justify-between items-center bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex items-center space-x-4">
                        <div className="p-3 bg-indigo-100 rounded-lg">
                            <ChartBarIcon className="w-8 h-8 text-indigo-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Analítica de Negocio</h1>
                            <p className="text-sm text-gray-500">Módulo avanzado de reportes y toma de decisiones</p>
                        </div>
                    </div>

                    {/* Section Selector */}
                    <div className="flex space-x-1 mt-4 md:mt-0 bg-gray-100 p-1.5 rounded-xl">
                        <button
                            onClick={() => setActiveSection('operational')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${activeSection === 'operational' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'}`}
                        >
                            <span className="flex items-center gap-2"><ClipboardDocumentListIcon className="w-4 h-4" /> Operativos</span>
                        </button>
                        <button
                            onClick={() => setActiveSection('analytics')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${activeSection === 'analytics' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'}`}
                        >
                            <span className="flex items-center gap-2"><ChartBarIcon className="w-4 h-4" /> Ventas Analíticas</span>
                        </button>
                        <button
                            onClick={() => setActiveSection('inventory')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${activeSection === 'inventory' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'}`}
                        >
                            <span className="flex items-center gap-2"><ChartPieIcon className="w-4 h-4" /> Inventario y Rotación</span>
                        </button>
                    </div>
                </div>

                {/* Unified Advanced Filters */}
                <AdvancedFilters 
                    filters={filters} 
                    onChange={setFilters} 
                    onApply={handleApplyFilters} 
                />

                {/* Sub-components rendered conditionally based on active section */}
                <div className="animate-fade-in">
                    {activeSection === 'operational' && (
                        <OperationalReports filters={appliedFilters} onPreview={handlePreview} />
                    )}
                    
                    {activeSection === 'analytics' && (
                        <AnalyticsReports filters={appliedFilters} onPreview={handlePreview} />
                    )}
                    
                    {activeSection === 'inventory' && (
                        <InventoryReports filters={appliedFilters} onPreview={handlePreview} />
                    )}
                </div>

                <ReportPreviewModal
                    isOpen={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    pdfUrl={previewUrl}
                    title={previewTitle}
                />
            </div>
        </Layout>
    );
};

export default ReportsPage;
